import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MenuData, RoleModel } from '@/types/menu';

/**
 * Permission Store State Interface
 * Manages user roles and granular permissions for feature-level access control
 */
export interface PermissionState {
  // User's assigned roles (e.g., ['admin', 'user'])
  roles: string[];
  
  // User's assigned role models
  roleModels: RoleModel[];
  
  // Map of feature/page ID to permissions user has for that feature
  // Example: { "recon-invoices": Set(["recon.invoices.read", "recon.invoices.write"]) }
  featurePermissions: Map<string, Set<string>>;
  
  // Map of route path to permissions (e.g., { "/discrepancies": Set(["view", "edit"]) })
  pathPermissions: Map<string, Set<string>>;
  
  // Flat set of all permissions for quick lookup
  // Example: Set(["recon.read", "recon.invoices.read", "analytics.read"])
  allPermissions: Set<string>;
  
  // Loading state
  isLoading: boolean;
  
  // Last update timestamp
  lastUpdated: number | null;
  
  // Actions
  setPermissions: (menuData: MenuData) => void;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasFeatureAccess: (featureId: string, requiredPermissions?: string[]) => boolean;
  getRoleDescription: (role: string) => string | null;
  getRoleModel: (role: string) => RoleModel | null;
  clearPermissions: () => void;
  reset: () => void;
}

/**
 * Permission Store
 * Caches user roles and permissions from the menu API response
 * Used by auth guards to check access on route navigation
 */
export const usePermissionStore = create<PermissionState>()(
  persist(
    (set, get) => ({
      roles: [],
      roleModels: [],
      featurePermissions: new Map(),
      pathPermissions: new Map(),
      allPermissions: new Set(),
      isLoading: false,
      lastUpdated: null,

      /**
       * Parse menu data and extract all permissions
       * This is called when the menu API response is received
       */
      setPermissions: (menuData: MenuData) => {        
        const featurePermissions = new Map<string, Set<string>>();
        const pathPermissions = new Map<string, Set<string>>();
        const allPermissions = new Set<string>();

        // Extract roles from menu data
        const roles = menuData.roles || [];

        // Parse permissions from menu groups and items
        if (menuData.groups && Array.isArray(menuData.groups)) {
          menuData.groups.forEach((group) => {
            if (!group.items || !Array.isArray(group.items)) {
              return;
            }

            // Process each menu item
            group.items.forEach((item) => {
              // Store permissions for this feature/item
              if (item.permissions && Array.isArray(item.permissions)) {
                const itemPermissions = new Set<string>(item.permissions);
                featurePermissions.set(item.id, itemPermissions);
                
                // Also store by path for top-level items
                if (item.path) {
                  const pathKey = item.path.startsWith('/') ? item.path.substring(1) : item.path;
                  pathPermissions.set(pathKey, itemPermissions);
                }
                
                // Add to flat list for quick lookup
                item.permissions.forEach((perm) => {
                  allPermissions.add(perm);
                });
              }
              
              // Process nested items (sub-menu items)
              if (item.items && Array.isArray(item.items)) {
                item.items.forEach((subItem) => {
                  
                  if (subItem.permissions && Array.isArray(subItem.permissions)) {
                    const subItemPermissions = new Set<string>(subItem.permissions);
                    featurePermissions.set(subItem.id, subItemPermissions);
                    
                    // Also store by path (extract last part of path)
                    if (subItem.path) {
                      const pathKey = subItem.path.startsWith('/') ? subItem.path.substring(1) : subItem.path;
                      pathPermissions.set(pathKey, subItemPermissions);
                    }
                    
                    // Add to flat list
                    subItem.permissions.forEach((perm) => {
                      allPermissions.add(perm);
                    });
                  }
                });
              }
            });
          });
        }

        // Extract role models with descriptions (for display purposes only, not for permissions)
        const roleModels = menuData.roleModels || [];
                
        set({
          roles,
          roleModels,
          featurePermissions,
          pathPermissions,
          allPermissions,
          isLoading: false,
          lastUpdated: Date.now(),
        });
      },

      /**
       * Check if user has a specific role
       */
      hasRole: (role: string) => {
        const state = get();
        return state.roles.includes(role);
      },

      /**
       * Check if user has a specific permission
       */
      hasPermission: (permission: string) => {
        const state = get();
        return state.allPermissions.has(permission);
      },

      /**
       * Check if user has ANY of the specified permissions
       * Returns true if user has at least one permission from the list
       */
      hasAnyPermission: (permissions: string[]) => {
        const state = get();
        return permissions.some((perm) => state.allPermissions.has(perm));
      },

      /**
       * Check if user has ALL of the specified permissions
       * Returns true only if user has every permission in the list
       */
      hasAllPermissions: (permissions: string[]) => {
        const state = get();
        return permissions.every((perm) => state.allPermissions.has(perm));
      },

      /**
       * Check if user has access to a specific feature
       * @param featureId - The feature/menu item ID to check
       * @param requiredPermissions - Optional specific permissions required
       * @returns true if user has access, false otherwise
       */
      hasFeatureAccess: (featureId: string, requiredPermissions?: string[]) => {
        const state = get();
        // Try to get permissions by feature ID first, then by path
        let featurePerms = state.featurePermissions.get(featureId);
        if (!featurePerms) {
          featurePerms = state.pathPermissions.get(featureId);
        }
        
        // If no specific permissions required, just check if feature exists in user's permissions
        if (!requiredPermissions || requiredPermissions.length === 0) {
          return featurePerms !== undefined && featurePerms.size > 0;
        }
        
        // Check if user has the required permissions for this feature
        if (!featurePerms) return false;
        
        // User must have at least one of the required permissions
        return requiredPermissions.some((perm) => featurePerms.has(perm));
      },

      /**
       * Get role description by role name
       * @param role - The role name to look up
       * @returns The role description or null if not found
       */
      getRoleDescription: (role: string) => {
        const state = get();
        const roleModel = state.roleModels.find((r) => r.role === role || r.id === role);
        return roleModel?.description || null;
      },

      /**
       * Get full role model by role name
       * @param role - The role name to look up
       * @returns The RoleModel or null if not found
       */
      getRoleModel: (role: string) => {
        const state = get();
        return state.roleModels.find((r) => r.role === role || r.id === role) || null;
      },

      /**
       * Clear all permissions (useful for logout)
       */
      clearPermissions: () => {
        set({
          roles: [],
          roleModels: [],
          featurePermissions: new Map(),
          pathPermissions: new Map(),
          allPermissions: new Set(),
          lastUpdated: null,
        });
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set({
          roles: [],
          roleModels: [],
          featurePermissions: new Map(),
          pathPermissions: new Map(),
          allPermissions: new Set(),
          isLoading: false,
          lastUpdated: null,
        });
      },
    }),
    {
      name: 'permission-storage',
      // Serialize Map and Set for localStorage
      partialize: (state) => ({
        roles: state.roles,
        roleModels: state.roleModels,
        featurePermissions: Array.from(state.featurePermissions.entries()).map(([key, value]) => [
          key,
          Array.from(value),
        ]),
        pathPermissions: Array.from(state.pathPermissions.entries()).map(([key, value]) => [
          key,
          Array.from(value),
        ]),
        allPermissions: Array.from(state.allPermissions),
        lastUpdated: state.lastUpdated,
      }),
      // Deserialize back to Map and Set
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.featurePermissions = new Map(
            (state.featurePermissions as any).map(([key, value]: [string, string[]]) => [
              key,
              new Set(value),
            ])
          );
          state.pathPermissions = new Map(
            (state.pathPermissions as any).map(([key, value]: [string, string[]]) => [
              key,
              new Set(value),
            ])
          );
          state.allPermissions = new Set(state.allPermissions as any);
        }
      },
    }
  )
);
